import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { workforceApi } from '../../services/api';
import { PageHeader, StatCard, Spinner, EmptyState } from '../../components/shared/index';
import {
  UsersIcon, UserMinusIcon, UserPlusIcon, ArrowTrendingUpIcon,
  ArrowRightIcon, ArrowPathIcon, AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const HORIZONS = [
  { value: 6,  label: '6 months' },
  { value: 12, label: '12 months' },
];

function gapChip(gap) {
  const n = Number(gap) || 0;
  if (n > 0) return { className: 'bg-red-50 text-red-700 border-red-200',     label: `+${n}` };
  if (n < 0) return { className: 'bg-blue-50 text-blue-700 border-blue-200',  label: `${n}` };
  return { className: 'bg-green-50 text-green-700 border-green-200', label: '0' };
}

function RoleBreakdown({ rows }) {
  if (!rows || rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => Math.max(Number(r.current) || 0, Number(r.projected) || 0)), 1);

  return (
    <div className="card p-5">
      <h2 className="section-title mb-4">Role breakdown</h2>
      <div className="space-y-3">
        {rows.map((r) => {
          const currentPct   = ((Number(r.current)   || 0) / max) * 100;
          const projectedPct = ((Number(r.projected) || 0) / max) * 100;
          const gap = Number(r.gap) || 0;
          return (
            <div key={r.role}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-gray-800">{r.role}</span>
                <span className="text-xs text-gray-500">
                  {r.current} → {r.projected}
                  {gap !== 0 && (
                    <span className={clsx(
                      'ml-2 font-semibold',
                      gap > 0 ? 'text-red-600' : 'text-blue-600',
                    )}>
                      (gap {gap > 0 ? '+' : ''}{gap})
                    </span>
                  )}
                </span>
              </div>
              <div className="relative h-5 bg-gray-100 rounded">
                <div className="absolute inset-y-0 left-0 bg-gray-300 rounded" style={{ width: `${currentPct}%` }} title={`Current: ${r.current}`} />
                <div className="absolute inset-y-0 left-0 bg-blue-500 rounded opacity-70" style={{ width: `${projectedPct}%` }} title={`Projected: ${r.projected}`} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-gray-300 rounded-sm" />Current</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-500 rounded-sm opacity-70" />Projected</span>
      </div>
    </div>
  );
}

export default function WorkforcePlanning() {
  const qc = useQueryClient();
  const [horizon, setHorizon] = useState(12);
  const [growthPct, setGrowthPct] = useState(10);

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['workforce', 'summary', horizon],
    queryFn:  () => workforceApi.summary(horizon).then((r) => r.data),
  });

  const { data: forecasts, isLoading: loadingForecasts } = useQuery({
    queryKey: ['workforce', 'forecasts', horizon],
    queryFn:  () => workforceApi.forecasts({ horizon }).then((r) => r.data),
  });

  const refresh = useMutation({
    mutationFn: workforceApi.refresh,
    onSuccess:  () => {
      toast.success('Forecast recompute queued');
      qc.invalidateQueries({ queryKey: ['workforce'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to queue recompute'),
  });

  if (loadingSummary) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  const totalGap = Number(summary?.total_hiring_gap) || 0;
  const bigGap = totalGap >= 5;

  const list = Array.isArray(forecasts) ? forecasts : (forecasts?.data || []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Workforce Planning"
        subtitle="AI-assisted forecasts covering attrition, hiring pipeline, and growth targets."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
              {HORIZONS.map((h) => (
                <button
                  key={h.value}
                  onClick={() => setHorizon(h.value)}
                  className={clsx(
                    'px-3 py-1.5 text-sm font-medium transition-colors',
                    horizon === h.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50',
                  )}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard title="Current headcount"    value={summary?.total_current   ?? 0} color="blue"   icon={UsersIcon} />
        <StatCard title={`Projected (${horizon}mo)`} value={summary?.total_projected ?? 0} color="purple" icon={ArrowTrendingUpIcon} />
        <StatCard title="Projected attrition"  value={summary?.total_attrition ?? 0} color="amber"  icon={UserMinusIcon} />
        <StatCard title="Open pipeline"        value={summary?.total_pipeline  ?? 0} color="green"  icon={UserPlusIcon} />
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-500">Hiring gap</p>
            <div className={clsx(
              'w-9 h-9 rounded-lg flex items-center justify-center',
              bigGap ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500',
            )}>
              <ExclamationTriangleIcon className="w-5 h-5" />
            </div>
          </div>
          <p className={clsx('text-3xl font-bold', bigGap ? 'text-red-600' : 'text-gray-900')}>
            {totalGap > 0 ? `+${totalGap}` : totalGap}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {totalGap > 0 ? 'hires needed to meet target' : totalGap < 0 ? 'surplus capacity' : 'in balance'}
          </p>
        </div>
      </div>

      {/* Recompute control */}
      <div className="card p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">Recompute forecast</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Adjust the growth target and queue a fresh forecast. Results refresh on the next planner tick.
            </p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="label">Growth target: <span className="font-semibold text-gray-900">{growthPct}%</span></label>
            <input
              type="range"
              min="5"
              max="25"
              step="1"
              value={growthPct}
              onChange={(e) => setGrowthPct(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>5%</span><span>10%</span><span>15%</span><span>20%</span><span>25%</span>
            </div>
          </div>
          <button
            onClick={() => refresh.mutate({ horizon, growth_target_pct: growthPct })}
            disabled={refresh.isPending}
            className="btn-primary"
          >
            <ArrowPathIcon className={clsx('w-4 h-4', refresh.isPending && 'animate-spin')} />
            {refresh.isPending ? 'Queuing…' : 'Recompute'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h2 className="section-title">Forecasts by role × department</h2>
            </div>
            {loadingForecasts ? (
              <div className="flex justify-center p-16"><Spinner size="lg" /></div>
            ) : list.length === 0 ? (
              <EmptyState
                title="No forecasts yet"
                description="Queue a recompute to generate forecasts for this horizon."
              />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      {['Role', 'Department', 'Current → Projected', 'Attrition', 'Pipeline', 'Gap'].map((h) => (
                        <th key={h} className="th">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {list.map((f) => {
                      const chip = gapChip(f.hiring_gap);
                      return (
                        <tr key={f.id} className="tr-hover">
                          <td className="td font-medium text-gray-900">{f.role}</td>
                          <td className="td text-sm text-gray-600">{f.department?.name || '—'}</td>
                          <td className="td">
                            <div className="inline-flex items-center gap-2 text-sm">
                              <span className="font-semibold text-gray-900">{f.current_headcount}</span>
                              <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                              <span className="font-semibold text-blue-600">{f.projected_headcount}</span>
                            </div>
                          </td>
                          <td className="td text-sm text-amber-700">{f.projected_attrition}</td>
                          <td className="td text-sm text-green-700">{f.open_pipeline}</td>
                          <td className="td">
                            <span className={clsx(
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border',
                              chip.className,
                            )}>
                              {chip.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div>
          <RoleBreakdown rows={summary?.role_breakdown} />
        </div>
      </div>
    </div>
  );
}
